/**
 * Spec 102 — collapsible before/after diff of a suggestion's `proposed_patch`
 * against the target rule's CURRENT field values.
 *
 * The "before" string is derived per `suggestion_type` from the joined rule
 * fields (`existing_rule_definition` / `existing_rule_evaluation_steps`); the
 * "after" string comes from the matching `proposed_patch` key. Unknown patch
 * shapes (or a missing target rule) fall back to rendering the raw patch JSON
 * in the "after" pane so the operator always sees the proposed payload.
 *
 * diff-match-patch (the lazy client dep introduced in spec 088c) is imported
 * dynamically inside the expand handler so it stays out of the initial bundle
 * critical path — it only loads when an operator actually expands a card.
 */
import { useCallback, useState } from 'react';
import { Icon } from '../../components/ui/Icon.js';
import type { RuleSuggestion, SuggestionType } from '../../api/ruleIngestion.js';

interface DiffSeg {
  op: -1 | 0 | 1;
  text: string;
}

/** Pretty-print a JSONB sub-value as readable plain text for diffing. */
function asText(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join('\n');
  if (typeof v === 'object') return JSON.stringify(v, null, 2);
  return String(v);
}

function rubricText(steps: Record<string, unknown> | null): string {
  if (!steps) return '';
  const rubric = steps['rubric'];
  if (!rubric || typeof rubric !== 'object') return '';
  return Object.entries(rubric as Record<string, unknown>)
    .map(([k, val]) => `${k}: ${asText(val)}`)
    .join('\n');
}

function stepsText(steps: Record<string, unknown> | null): string {
  if (!steps) return '';
  return asText(steps['evaluation_steps']);
}

/**
 * Compute (before, after) plain-text pair for a suggestion, by type.
 * Returns `raw: true` when we could not map a structured field pair and fell
 * back to raw-JSON rendering of the proposed patch.
 */
export function computeBeforeAfter(s: RuleSuggestion): {
  before: string;
  after: string;
  label: string;
  raw: boolean;
} {
  const patch = (s.proposed_patch ?? {}) as Record<string, unknown>;
  const type: SuggestionType = s.suggestion_type;

  if (type === 'definition_refinement' && typeof patch['definition'] === 'string') {
    return {
      before: s.existing_rule_definition ?? '',
      after: asText(patch['definition']),
      label: 'definition',
      raw: false,
    };
  }
  if (
    (type === 'rubric_change' || type === 'evaluation_steps_change') &&
    typeof patch['proposed_rubric_update'] === 'string'
  ) {
    const before =
      type === 'rubric_change'
        ? rubricText(s.existing_rule_evaluation_steps)
        : stepsText(s.existing_rule_evaluation_steps);
    return {
      before,
      after: asText(patch['proposed_rubric_update']),
      label: type === 'rubric_change' ? 'rubric' : 'evaluation_steps',
      raw: false,
    };
  }
  // provenance_only + any unrecognized shape → raw JSON fallback.
  return {
    before: '',
    after: JSON.stringify(patch, null, 2),
    label: type === 'provenance_only' ? 'provenance' : 'patch',
    raw: true,
  };
}

function DiffText({ segs, mode }: { segs: DiffSeg[]; mode: 'before' | 'after' }) {
  // before pane shows EQUAL + DELETE; after pane shows EQUAL + INSERT.
  return (
    <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed m-0">
      {segs.map((seg, i) => {
        if (seg.op === 0) return <span key={i}>{seg.text}</span>;
        if (seg.op === -1 && mode === 'before') {
          return (
            <span key={i} className="bg-rose-100 text-rose-800 line-through">
              {seg.text}
            </span>
          );
        }
        if (seg.op === 1 && mode === 'after') {
          return (
            <span key={i} className="bg-emerald-100 text-emerald-800">
              {seg.text}
            </span>
          );
        }
        return null;
      })}
    </pre>
  );
}

export function SuggestionDiff({ suggestion }: { suggestion: RuleSuggestion }) {
  const [open, setOpen] = useState(false);
  const [segs, setSegs] = useState<DiffSeg[] | null>(null);
  const [meta, setMeta] = useState<{ before: string; after: string; label: string; raw: boolean } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const onToggle = useCallback(async () => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    if (segs) return; // already computed
    setLoading(true);
    try {
      const ba = computeBeforeAfter(suggestion);
      setMeta(ba);
      // Lazy-load diff-match-patch (spec 088c dep) only on first expand.
      const mod = await import('diff-match-patch');
      const Dmp = (mod as unknown as { default: new () => unknown }).default;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dmp: any = new (Dmp as any)();
      const diffs = dmp.diff_main(ba.before, ba.after);
      dmp.diff_cleanupSemantic(diffs);
      setSegs(diffs.map((d: [number, string]) => ({ op: d[0] as -1 | 0 | 1, text: d[1] })));
    } catch {
      // Fallback: render plain before/after with no inline highlight.
      const ba = computeBeforeAfter(suggestion);
      setMeta(ba);
      setSegs([
        { op: -1, text: ba.before },
        { op: 1, text: ba.after },
      ]);
    } finally {
      setLoading(false);
    }
  }, [open, segs, suggestion]);

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={onToggle}
        data-testid="suggestion-diff-toggle"
        className="hub-btn hub-btn--ghost"
        aria-expanded={open}
      >
        {open ? <Icon name="bx-chevron-down" /> : <Icon name="bx-chevron-right" />}
        <Icon name="bx-git-compare" className="text-slate-500" />
        {open ? 'Hide' : 'Show'} before/after diff
      </button>

      {open && (
        <div
          data-testid={`suggestion-diff-${suggestion.id}`}
          className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2"
        >
          {loading && <div className="text-xs text-slate-500">Computing diff…</div>}
          {!loading && segs && (
            <>
              <div className="rounded-md border border-slate-200 bg-white p-2">
                <div className="mb-1 text-xs font-semibold text-slate-400">
                  {meta?.raw ? 'Current (none for this type)' : `Current ${meta?.label}`}
                </div>
                <div data-testid="suggestion-diff-before">
                  {meta?.raw ? (
                    <span className="text-xs text-slate-400">
                      No field-level before state for {suggestion.suggestion_type}.
                    </span>
                  ) : (
                    <DiffText segs={segs} mode="before" />
                  )}
                </div>
              </div>
              <div className="rounded-md border border-slate-200 bg-white p-2">
                <div className="mb-1 text-xs font-semibold text-slate-400">
                  Proposed {meta?.label}
                </div>
                <div data-testid="suggestion-diff-after">
                  <DiffText segs={segs} mode="after" />
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
